import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { PageComponent } from "@components/page/page.component";
import { SearchComponent } from "@components/search/search.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { StatedataService, Volatile } from "~app/common/services/statedata.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { LeverantorsorderListComponent } from "./components/leverantorsorder-list/leverantorsorder-list.component";
import { CreateLeverantorsorderModalComponent } from "./create-leverantorsorder-modal/create-leverantorsorder-modal.component";
import { LeverantorsorderCreatedMessage } from "./events/LeverantorsorderCreatedMessage";
import { ILeverantorsorderListResponse, LeverantorsorderListRequest } from "./models/leverantororder-models";
import { LeverantorsorderService } from "./services/leverantorsorder.service";

@Component({
    selector: "rask-leverantorsorder",
    templateUrl: "./leverantorsorder.component.html",
    styleUrls: ["./leverantorsorder.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        TogglePanelComponent,
        SearchComponent,
        DatepickerComponent,
        SelectorComponent,
        SubmitButtonComponent,
        ButtonComponent,
        LeverantorsorderListComponent,
        TranslatePipe
    ]
})
export class LeverantorsorderComponent implements OnInit {
    baseData: any = null;
    form: UntypedFormGroup;
    leverantorsorderSearchParams: LeverantorsorderListRequest;
    leverantorsordrar: ILeverantorsorderListResponse[] = null;
    volatile: Volatile;

    private readonly WIK = "Leverantorsorder.searchparams";
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly eventService = inject(EventService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly leverantorsorderService = inject(LeverantorsorderService);
    private readonly modalService = inject(RaskModalService);
    private readonly router = inject(Router);
    private readonly stateDataService = inject(StatedataService);
    private readonly translateService = inject(TranslateService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        this.volatile = this.stateDataService.getVolatile();
        this.leverantorsorderSearchParams = new LeverantorsorderListRequest();
    }

    canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    create = (): void => {
        this.modalService.open(this.translateService.instant("LEVERANTORSORDER_LIST_SKAPANYLEV"), CreateLeverantorsorderModalComponent, "xl", true);
    };

    formatter = (x: any) => x ? x.id : undefined;

    ngOnInit() {
        this.leverantorsorderService.getLeverantorsorderGrunddata().subscribe(data => {
            this.baseData = data;
        });
        this.leverantorsorderSearchParams = this.volatile.get<LeverantorsorderListRequest>(this.WIK);
        if (this.leverantorsorderSearchParams) {
            this.form = this.formBuilder.group(this.leverantorsorderSearchParams);
            this.search(false);
            return;
        }

        this.resetToWebinstallning();

        this.eventService.subscribeEvent<LeverantorsorderCreatedMessage>(LeverantorsorderCreatedMessage.$type).subscribe(k => {
            this.router.navigate([
                "leverantorsorder",
                k.orderId
            ]);
        });
    }

    outputformatter = (x: any) => x ? x.namn : undefined;

    reset() {
        this.leverantorsorderSearchParams = new LeverantorsorderListRequest();
        this.form.get("sok").setValue(null);
        this.form.get("leverantorsorder").setValue(null);
        this.form.get("leverantor").setValue(null);
        this.form.get("periodFrom").setValue(null);
        this.form.get("periodTom").setValue(null);
        this.form.get("obalans").setValue(false);
        this.form.get("ordernummer").setValue(null);
        this.form.get("avtalsansvarig").setValue(null);
        this.form.get("handelssortimentsgrupp").setValue(null);
        this.form.get("mottagningsplats").setValue(null);
        this.leverantorsordrar = null;
    }

    resetToWebinstallning() {
        let searchParams = this.webinstallningarService.get<LeverantorsorderListRequest>(this.WIK);
        if (searchParams == null) {
            searchParams = new LeverantorsorderListRequest();
        }
        this.leverantorsorderSearchParams = searchParams;
        this.form = this.formBuilder.group(this.leverantorsorderSearchParams);
    }

    search(saveState = true) {
        const query = this.leverantorsorderService.searchForLeverantorsorder(this.form.value);
        this.busyService.addWork(query).subscribe(res => {
            this.leverantorsordrar = res as any;
        });

        if (saveState) {
            this.webinstallningarService.set<LeverantorsorderListRequest>(this.WIK, this.form.value);
            this.volatile.set(this.WIK, this.form.value);
        }
    }

    searchAvtalsansvarigTypeahead = (searchString: string) => this.leverantorsorderService.searchForAvtalsansvarigTypeahead({ query: searchString });

    searchAvtalsansvarigTypeaheadValue = (value: number) => this.leverantorsorderService.searchForAvtalsansvarigTypeahead({ id: value });

    searchLeverantorTypeAhead = (searchString: string) => this.leverantorsorderService.searchForLeverantorTypeAhead({ query: searchString });

    searchLeverantorTypeAheadValue = (value: number) => this.leverantorsorderService.searchForLeverantorTypeAhead({ id: value });

    searchMottagningsplatsTypeAhead = (searchString: string) => this.leverantorsorderService.searchForMottagningsplatsTypeAhead({ input: searchString });

    searchMottagningsplatsTypeAheadValue = (value: number) => this.leverantorsorderService.searchForMottagningsplatsTypeAhead({ id: value });

    searchOrdernummerTypeAhead = (searchString: string) => this.leverantorsorderService.searchForOrdernummerTypeahead({ query: searchString });

    searchOrdernummerTypeAheadValue = (value: number) => this.leverantorsorderService.searchForOrdernummerTypeahead({ id: value });
}
